import React, { useState, useEffect } from 'react';
import { FiTruck, FiZap, FiClock, FiPackage, FiCheck } from 'react-icons/fi';
import shippingService from '../../services/shipping.service';

const CARRIER_ICONS = {
  economy: FiClock,
  standard: FiTruck,
  express: FiZap,
  overnight: FiPackage,
};

/**
 * ShippingRateSelector
 * Props:
 *   cartItems       - [{ productId, quantity }]
 *   subtotal        - cart subtotal
 *   country         - destination country code
 *   selectedRate    - currently selected rate id
 *   onSelect        - callback(rate)
 */
const ShippingRateSelector = ({ cartItems = [], subtotal = 0, country = 'US', selectedRate, onSelect }) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRates = async () => {
      if (!cartItems.length) return;
      setLoading(true);
      try {
        const items = cartItems.map(i => ({ productId: i.productId?._id || i.productId, quantity: i.quantity }));
        const res = await shippingService.getRates(items, country, subtotal);
        const data = res.data.data || [];
        setRates(data);
        // Auto-select cheapest
        if (data.length > 0 && !selectedRate) {
          onSelect?.(data[0]);
        }
      } catch { setRates([]); }
      setLoading(false);
    };
    fetchRates();
  }, [cartItems.length, country, subtotal]);

  if (loading) {
    return (
      <div className="text-center py-3 text-muted" style={{ fontSize: '13px' }}>
        <FiTruck size={16} className="me-2 spin" /> Calculating shipping rates...
      </div>
    );
  }

  if (!rates.length) {
    return (
      <div className="text-center py-3 text-muted" style={{ fontSize: '13px' }}>
        No shipping options available for your location.
      </div>
    );
  }

  return (
    <div>
      <label style={{ fontSize: '14px', fontWeight: 700, marginBottom: '10px', display: 'block' }}>
        <FiTruck size={15} className="me-1" color="#B91C1C" /> Shipping Method
      </label>

      <div className="d-flex flex-column gap-2">
        {rates.map(rate => {
          const Icon = CARRIER_ICONS[rate.id] || FiTruck;
          const isSelected = selectedRate?.id === rate.id;

          return (
            <div
              key={rate.id}
              onClick={() => onSelect?.(rate)}
              style={{
                border: isSelected ? '2px solid #B91C1C' : '1px solid #E5E7EB',
                borderRadius: '10px',
                padding: '12px 16px',
                cursor: 'pointer',
                background: isSelected ? '#FEF2F2' : '#FFF',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div style={{
                width: '36px', height: '36px', borderRadius: '8px',
                background: isSelected ? '#B91C1C' : '#F3F4F6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: isSelected ? '#FFF' : '#6B7280',
                flexShrink: 0,
              }}>
                <Icon size={18} />
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '14px', color: '#1F2937' }}>
                  {rate.name}
                  <span style={{ fontSize: '11px', fontWeight: 400, color: '#9CA3AF', marginLeft: '6px' }}>
                    ({rate.carrier})
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                  {rate.estimatedDays} · Est. {rate.estimatedDate}
                </div>
              </div>

              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                {rate.isFree ? (
                  <span style={{ fontWeight: 800, fontSize: '14px', color: '#059669' }}>FREE</span>
                ) : (
                  <span style={{ fontWeight: 800, fontSize: '14px', color: '#1F2937' }}>${rate.rate.toFixed(2)}</span>
                )}
              </div>

              {isSelected && (
                <FiCheck size={18} color="#B91C1C" strokeWidth={3} style={{ flexShrink: 0 }} />
              )}
            </div>
          );
        })}
      </div>

      {rates[0]?.totalWeight > 0 && (
        <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '8px', marginBottom: 0 }}>
          📦 Total weight: {rates[0].totalWeight} kg
        </p>
      )}
    </div>
  );
};

export default ShippingRateSelector;
